import { useNavigate, useLocation } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { setApiEmployer } from "../../lib/api";
import {
    employerAllows, employerName, grantedEmployerModules, clearEmployerContext,
} from "../../lib/employerModules";

/**
 * EmployeeContextBanner — zeigt einem Mitarbeiter (Vermittler), der im
 * Arbeitgeber-Kontext in einem echten Händler-Modul arbeitet, einen gut
 * sichtbaren Hinweis + Schnellwechsler zwischen allen freigegebenen Modulen
 * + „Zurück zum Firmen-Bereich" (beendet den Kontext).
 *
 * Rendert nichts, wenn kein Mitarbeiter-Kontext aktiv ist (b2b/admin sehen es nie).
 */
export default function EmployeeContextBanner() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // null als requiredPerm = nur prüfen, ob überhaupt ein Arbeitgeber-Kontext aktiv ist.
    if (!employerAllows(user, null)) return null;

    const modules = grantedEmployerModules();
    const name = employerName();
    // Feste Mitarbeiter (zweiradverkaeufer) haben keinen Vermittler-Bereich —
    // „Zurück" führt für sie ins Mitarbeiter-Dashboard statt nach /vermittler
    // (das leitet Nicht-Vermittler auf die Startseite um → gemeldeter Bug).
    const isFestangestellt = user.role === "zweiradverkaeufer";

    const exit = () => {
        setApiEmployer(null);
        clearEmployerContext();
        navigate(isFestangestellt ? "/dashboard" : "/vermittler");
    };

    return (
        <div className="bg-[#FFCC00] text-[#111827]" data-testid="employee-context-banner">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-2.5 flex items-center gap-3 flex-wrap">
                <Building2 className="w-4 h-4 shrink-0" />
                <span className="text-sm font-bold">
                    Mitarbeiter-Kontext: <span className="underline">{name || "Arbeitgeber"}</span>
                </span>
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="employee-context-switchers">
                    {modules.map((m) => {
                        const active = location.pathname === m.path;
                        return (
                            <button
                                key={m.path}
                                onClick={() => !active && navigate(m.path)}
                                className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm transition-colors ${
                                    active
                                        ? "bg-[#111827] text-white"
                                        : "bg-black/10 text-[#111827] hover:bg-black/20"
                                }`}
                                data-testid={`employee-switch-${m.path.replace(/\//g, "-").replace(/^-/, "")}`}
                            >
                                {m.label}
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={exit}
                    className="ml-auto text-xs font-bold uppercase tracking-wider bg-[#111827] text-white px-3 py-1.5 rounded-sm hover:bg-black"
                    data-testid="employee-context-exit"
                >
                    ← {isFestangestellt ? "Mein Dashboard" : "Firmen-Bereich"}
                </button>
            </div>
        </div>
    );
}
